//#region Imports
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import { MarketplaceResultType } from "config";
import type { MarketplaceSearchRequestJSON } from "marketplace/_schema/marketplace-search-request-schema";
import MarketplaceSearchResponseSchema from "marketplace/_schema/marketplace-search-response-schema";
import { z } from "zod";
//#endregion

export type AssistedSearchFiltersJSON = z.infer<typeof AssistedSearchSchema.FiltersZ>;
export type AssistedSearchIntentJSON = z.infer<typeof AssistedSearchSchema.IntentZ>;
export type AssistedSearchRequestJSON = z.infer<typeof AssistedSearchSchema.RequestZ>;
export type AssistedSearchResponseJSON = z.infer<typeof AssistedSearchSchema.ResponseZ>;

export const AssistedSearchModes = ["Literal", "Assisted"] as const;
export type AssistedSearchMode = (typeof AssistedSearchModes)[number];

export const AssistedSearchFallbackReasons = [
  "None",
  "FeatureDisabled",
  "IntentParseUnavailable",
  "IntentParseTimedOut",
  "EmbeddingUnavailable",
  "SemanticRetrievalUnavailable",
  "RerankUnavailable",
  "DeadlineExceeded",
  "NoAuthorizedCandidates",
] as const;
export type AssistedSearchFallbackReason = (typeof AssistedSearchFallbackReasons)[number];

export const AssistedFilterKeys = [
  "city",
  "state",
  "regionsRegistered",
  "regionsLicensed",
  "yearsExperience",
  "fluentLanguages",
  "technicalLanguages",
  "school",
  "technicalBackgrounds",
  "practiceAreas",
  "isOnCall",
  "collaborates",
  "applicationsDrafted",
  "allowedApplications",
  "degree",
  "professionalRegistration",
  "insuredAmount",
  "billingUnit",
  "yearEstablished",
  "numberOfEmployees",
  "minimumBillingRate",
  "maximumBillingRate",
  "potentialDiversityCandidate",
  "entityTypes",
] as const;

export type AssistedFilterKey = (typeof AssistedFilterKeys)[number];
export const SuppressedInferencesQueryParam = "suppressedInferences";

const assistedFilterKeySet = new Set<string>(AssistedFilterKeys);

const assistedFilterLabels: Record<AssistedFilterKey, string> = {
  city: "City",
  state: "State",
  regionsRegistered: "Regions Registered In",
  regionsLicensed: "States Licensed In",
  yearsExperience: "Years Experience",
  fluentLanguages: "Fluent Languages",
  technicalLanguages: "Technical Languages",
  school: "Undergrad/Law School",
  technicalBackgrounds: "Technical Background",
  practiceAreas: "Practice Area",
  isOnCall: "On Call",
  collaborates: "Willing To Collaborate",
  applicationsDrafted: "Applications Drafted",
  allowedApplications: "Allowed Applications",
  degree: "Degree",
  professionalRegistration: "Professional Registration #",
  insuredAmount: "Minimum Insurance",
  billingUnit: "Billing Unit",
  yearEstablished: "Established No Later Than",
  numberOfEmployees: "Minimum Employees",
  minimumBillingRate: "Minimum Billing Rate",
  maximumBillingRate: "Maximum Billing Rate",
  potentialDiversityCandidate: "Diversity Candidate",
  entityTypes: "Profile Types",
};

/**
 * The response from `POST marketplace/search/assist`.
 *
 * Mirrors the API's assisted search contract. `parsedIntent` is the parser output, while
 * `effectiveFilters` is the filter set produced after explicit values and suppressions are
 * resolved.
 */
export default class AssistedSearchSchema {
  public static readonly FiltersZ = z.object({
    city: z.string().nullish(),
    state: z.string().nullish(),
    regionsRegistered: z.array(z.string()).nullish(),
    regionsLicensed: z.array(z.string()).nullish(),
    yearsExperience: z.number().nullish(),
    fluentLanguages: z.array(z.string()).nullish(),
    technicalLanguages: z.array(z.string()).nullish(),
    school: z.string().nullish(),
    technicalBackgrounds: z.array(z.string()).nullish(),
    practiceAreas: z.array(z.string()).nullish(),
    isOnCall: z.boolean().nullish(),
    collaborates: z.boolean().nullish(),
    applicationsDrafted: z.number().nullish(),
    allowedApplications: z.number().nullish(),
    degree: z.string().nullish(),
    professionalRegistration: z.string().nullish(),
    insuredAmount: z.number().nullish(),
    billingUnit: z.string().nullish(),
    yearEstablished: z.number().nullish(),
    numberOfEmployees: z.number().nullish(),
    minimumBillingRate: z.number().nullish(),
    maximumBillingRate: z.number().nullish(),
    potentialDiversityCandidate: z.boolean().nullish(),
    entityTypes: z.array(z.enum(MarketplaceResultType)).nullish(),
  });

  public static readonly IntentZ = z.object({
    inferredFilters: AssistedSearchSchema.FiltersZ,
    searchTerms: z.string().nullish(),
    declinedConstraints: z.array(z.string()).nullish(),
  });

  public static readonly RequestZ = z.object({
    query: z.string(),
    explicitFilters: AssistedSearchSchema.FiltersZ,
    suppressedInferences: z.array(z.enum(AssistedFilterKeys)),
    from: z.number(),
    size: z.number(),
    sortBy: z.string().nullish(),
  });

  public static readonly ResponseZ = z.object({
    modeUsed: z.enum(AssistedSearchModes),
    parsedIntent: AssistedSearchSchema.IntentZ,
    effectiveFilters: AssistedSearchSchema.FiltersZ,
    supersededInferences: z.array(z.enum(AssistedFilterKeys)),
    hits: z.array(
      z.object({
        data: MarketplaceSearchResponseSchema.SearchInfoJSONZ,
        score: z.number().nullish(),
        matchedBy: z.enum(["Literal", "Semantic", "Both"]).nullish(),
        matchedFields: z.array(z.string()).nullish(),
      }),
    ),
    total: z.number(),
    from: z.number(),
    size: z.number(),
    tookMs: z.number(),
    fallbackReason: z.enum(AssistedSearchFallbackReasons).nullish(),
  });

  public static validateJSON(json: unknown): AssistedSearchResponseJSON {
    return parseOrThrow(AssistedSearchSchema.ResponseZ, json, {
      logError: true,
      schemaName: "AssistedSearchSchema",
      operation: "validateJSON",
      payloadSample: json,
    });
  }

  public static isValidJSON(json: unknown): json is AssistedSearchResponseJSON {
    return AssistedSearchSchema.ResponseZ.safeParse(json).success;
  }
}

/** A parsed filter rendered for the user, with the query-string key needed to remove it. */
export type ParsedFilterChip = {
  filterName: AssistedFilterKey;
  label: string;
};

export function isAssistedFilterKey(value: string): value is AssistedFilterKey {
  return assistedFilterKeySet.has(value);
}

export function getAssistedFilterLabel(filterName: string): string {
  return isAssistedFilterKey(filterName) ? assistedFilterLabels[filterName] : filterName;
}

/**
 * Projects the ordinary Marketplace request onto the assisted endpoint's closed filter contract.
 * Unknown Marketplace fields are deliberately not copied: the server rejects unknown members, and
 * silently widening this list would make a filter appear supported before assisted search can
 * enforce it.
 */
export function toAssistedExplicitFilters(request: MarketplaceSearchRequestJSON): AssistedSearchFiltersJSON {
  const hasEntityTypeChoice = Boolean(request.hideCompanies || request.hideIndividuals || request.hideTeams);
  const entityTypes = hasEntityTypeChoice
    ? Object.values(MarketplaceResultType).filter((type) => {
        if (type === MarketplaceResultType.Company) return !request.hideCompanies;
        if (type === MarketplaceResultType.Individual) return !request.hideIndividuals;
        return !request.hideTeams;
      })
    : undefined;

  return AssistedSearchSchema.FiltersZ.parse({
    city: request.city,
    state: request.state,
    regionsRegistered: request.regionsRegistered,
    regionsLicensed: request.regionsLicensed,
    yearsExperience: request.yearsExperience,
    fluentLanguages: request.fluentLanguages,
    technicalLanguages: request.technicalLanguages,
    school: request.school,
    technicalBackgrounds: request.technicalBackgrounds,
    practiceAreas: request.practiceAreas,
    isOnCall: request.isOnCall,
    collaborates: request.collaborates,
    applicationsDrafted: request.applicationsDrafted,
    allowedApplications: request.allowedApplications,
    degree: request.degree,
    professionalRegistration: request.professionalRegistration,
    insuredAmount: request.insuredAmount,
    billingUnit: request.billingUnit,
    yearEstablished: request.yearEstablished,
    numberOfEmployees: request.numberOfEmployees,
    minimumBillingRate: request.minimumBillingRate,
    maximumBillingRate: request.maximumBillingRate,
    potentialDiversityCandidate: request.potentialDiversityCandidate,
    entityTypes,
  });
}

/**
 * Describes parser inferences that the caller has determined still apply.
 *
 * The user has to be able to see what the parser did to their sentence and undo any of it, so each
 * chip carries the query parameter that turns it off. A filter with no removable representation
 * would be worse than no filter at all - the results would be narrowed by something invisible.
 */
export function toParsedFilterChips(filters: AssistedSearchFiltersJSON | undefined): ParsedFilterChip[] {
  if (!filters) return [];

  const chips: ParsedFilterChip[] = [];

  const addText = (filterName: AssistedFilterKey, title: string, value: string | null | undefined) => {
    if (value) chips.push({ filterName, label: `${title}: ${value}` });
  };
  const addNumber = (filterName: AssistedFilterKey, title: string, value: number | null | undefined) => {
    if (value !== null && value !== undefined) chips.push({ filterName, label: `${title}: ${value}` });
  };
  const addList = (filterName: AssistedFilterKey, title: string, values: string[] | null | undefined) => {
    if (values && values.length > 0) chips.push({ filterName, label: `${title}: ${values.join(", ")}` });
  };
  const addBool = (filterName: AssistedFilterKey, title: string, value: boolean | null | undefined) => {
    if (value !== null && value !== undefined) {
      chips.push({ filterName, label: `${title}: ${value ? "Yes" : "No"}` });
    }
  };

  addText("city", "City", filters.city);
  addText("state", "State", filters.state);
  addNumber("yearsExperience", "Years Experience", filters.yearsExperience);
  addList("fluentLanguages", "Fluent Languages", filters.fluentLanguages);
  addList("technicalLanguages", "Technical Languages", filters.technicalLanguages);
  addList("regionsLicensed", "States Licensed In", filters.regionsLicensed);
  addList("regionsRegistered", "Regions Registered In", filters.regionsRegistered);
  addText("school", "Undergrad/Law School", filters.school);
  addList("technicalBackgrounds", "Technical Background", filters.technicalBackgrounds);
  addList("practiceAreas", "Practice Area", filters.practiceAreas);
  addNumber("applicationsDrafted", "Applications Drafted", filters.applicationsDrafted);
  addNumber("allowedApplications", "Allowed Applications", filters.allowedApplications);
  addText("degree", "Degree", filters.degree);
  addText("professionalRegistration", "Professional Registration #", filters.professionalRegistration);
  addNumber("insuredAmount", "Minimum Insurance", filters.insuredAmount);
  addText("billingUnit", "Billing Unit", filters.billingUnit);
  addNumber("yearEstablished", "Established No Later Than", filters.yearEstablished);
  addNumber("numberOfEmployees", "Minimum Employees", filters.numberOfEmployees);
  addBool("isOnCall", "On Call", filters.isOnCall);
  addBool("collaborates", "Willing To Collaborate", filters.collaborates);
  addBool("potentialDiversityCandidate", "Diversity Candidate", filters.potentialDiversityCandidate);
  addList("entityTypes", "Profile Types", filters.entityTypes);

  if (filters.minimumBillingRate !== null && filters.minimumBillingRate !== undefined) {
    chips.push({ filterName: "minimumBillingRate", label: `Billing Rate: from $${filters.minimumBillingRate}` });
  }
  if (filters.maximumBillingRate !== null && filters.maximumBillingRate !== undefined) {
    chips.push({ filterName: "maximumBillingRate", label: `Billing Rate: up to $${filters.maximumBillingRate}` });
  }

  return chips;
}
